import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BackArrow from '../../../BackArrow';
import { host_url } from '../../../App';
import './LogistikDienstplaene.css';

const CAMP_TAGE = [
  { datum: '2026-07-01', label: 'Mi 01.07.', lang: 'Mittwoch, 1. Juli' },
  { datum: '2026-07-02', label: 'Do 02.07.', lang: 'Donnerstag, 2. Juli' },
  { datum: '2026-07-03', label: 'Fr 03.07.', lang: 'Freitag, 3. Juli' },
  { datum: '2026-07-04', label: 'Sa 04.07.', lang: 'Samstag, 4. Juli' },
  { datum: '2026-07-05', label: 'So 05.07.', lang: 'Sonntag, 5. Juli' },
  { datum: '2026-07-06', label: 'Mo 06.07.', lang: 'Montag, 6. Juli' },
  { datum: '2026-07-07', label: 'Di 07.07.', lang: 'Dienstag, 7. Juli' },
  { datum: '2026-07-08', label: 'Mi 08.07.', lang: 'Mittwoch, 8. Juli' },
  { datum: '2026-07-09', label: 'Do 09.07.', lang: 'Donnerstag, 9. Juli' },
  { datum: '2026-07-10', label: 'Fr 10.07.', lang: 'Freitag, 10. Juli' },
  { datum: '2026-07-11', label: 'Sa 11.07.', lang: 'Samstag, 11. Juli' },
];

const VIEWS = [
  { mode: 'person', path: '/dienstplaene/logistik/person', icon: 'person_search', label: 'Person suchen' },
  { mode: 'tag', path: '/dienstplaene/logistik/tag', icon: 'calendar_today', label: 'Tag suchen' },
  { mode: 'dienst', path: '/dienstplaene/logistik/dienst', icon: 'work', label: 'Dienst suchen' },
  { mode: 'gesamt', path: '/dienstplaene/logistik/gesamt', icon: 'view_week', label: 'Gesamtübersicht' },
  { mode: 'fahrten', path: '/dienstplaene/logistik/fahrten', icon: 'directions_bus', label: 'Fahrten' },
];

const HUB_LINKS = [
  ...VIEWS,
  { mode: 'spuelplan', path: '/spuelplan', icon: 'local_dining', label: 'Spülplan gesamt (Luca)', secondary: true },
];

function tagAlsString(tag) {
  if (!tag) return '';
  if (typeof tag === 'string') return tag.substring(0, 10);
  return new Date(tag).toISOString().substring(0, 10);
}

function formatTime(value) {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const timeMatch = raw.match(/^(\d{1,2}):(\d{2})/);
  if (timeMatch) return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
  const numeric = Number(raw.replace(',', '.'));
  if (Number.isFinite(numeric)) {
    const fraction = numeric >= 1 ? numeric % 1 : numeric;
    if (fraction > 0 && fraction < 1) {
      const totalMinutes = Math.round(fraction * 24 * 60) % (24 * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    if (numeric >= 0 && numeric <= 23 && Number.isInteger(numeric)) {
      return `${String(numeric).padStart(2, '0')}:00`;
    }
  }
  return raw;
}

function zeitraum(eintrag) {
  const von = formatTime(eintrag.UhrzeitVon);
  const bis = formatTime(eintrag.UhrzeitBis);
  if (von && bis && von !== bis) return `${von}-${bis}`;
  return von || bis || '';
}

function tagInfo(tag) {
  return CAMP_TAGE.find(campTag => campTag.datum === tag) || { datum: tag, label: tag, lang: tag };
}

function heutigesDatum() {
  const heute = new Date();
  const jahr = heute.getFullYear();
  const monat = String(heute.getMonth() + 1).padStart(2, '0');
  const tag = String(heute.getDate()).padStart(2, '0');
  return `${jahr}-${monat}-${tag}`;
}

function heuteOderErsterTag() {
  const heute = heutigesDatum();
  if (CAMP_TAGE.some(tag => tag.datum === heute)) return heute;
  return CAMP_TAGE[5].datum;
}

function normalisiereName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function parsePersonen(value) {
  return String(value || '')
    .split(/\r?\n|;|,/)
    .map(person => person.trim())
    .filter(Boolean);
}

function personPasst(value, suche) {
  const ziel = normalisiereName(suche);
  if (!ziel) return false;
  return parsePersonen(value).some(person => normalisiereName(person).includes(ziel));
}

function sortNachTagUndZeit(a, b) {
  const tagA = a.AnzeigeTag || a.Tag || '';
  const tagB = b.AnzeigeTag || b.Tag || '';
  if (tagA !== tagB) return tagA.localeCompare(tagB);
  const zeitA = a.UhrzeitVon || a.Abfahrt || '';
  const zeitB = b.UhrzeitVon || b.Abfahrt || '';
  if (zeitA !== zeitB) return zeitA.localeCompare(zeitB);
  return String(a.JobName || a.Ziel || '').localeCompare(String(b.JobName || b.Ziel || ''), 'de');
}

function aktiveDienstKeys(dienste) {
  return new Set(dienste
    .filter(dienst => dienst.Aktiv !== false && dienst.Aktiv !== 0)
    .map(dienst => normalisiereName(dienst.Name)));
}

function useLogistikDaten() {
  const [schichten, setSchichten] = useState([]);
  const [fahrten, setFahrten] = useState([]);
  const [dienste, setDienste] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    let aktiv = true;
    setLaden(true);
    setFehler('');
    Promise.all([
      fetch(`${host_url}/logistik/schicht`).then(r => r.json()),
      fetch(`${host_url}/logistik/fahrten`).then(r => r.json()),
      fetch(`${host_url}/logistik/dienste?alle=1`).then(r => r.json()),
    ])
      .then(([schichtData, fahrtenData, dienstData]) => {
        if (!aktiv) return;
        setSchichten(Array.isArray(schichtData) ? schichtData : []);
        setFahrten(Array.isArray(fahrtenData) ? fahrtenData : []);
        setDienste(Array.isArray(dienstData) ? dienstData : []);
      })
      .catch(() => {
        if (aktiv) setFehler('Daten konnten nicht geladen werden.');
      })
      .finally(() => {
        if (aktiv) setLaden(false);
      });

    return () => {
      aktiv = false;
    };
  }, []);

  const dienstInfo = useMemo(() => {
    const map = new Map();
    dienste
      .filter(dienst => dienst.Aktiv !== false && dienst.Aktiv !== 0)
      .forEach(dienst => {
        const key = normalisiereName(dienst.Name);
        if (!map.has(key) || dienst.ScopeKey === 'ALL') map.set(key, dienst);
      });
    return map;
  }, [dienste]);

  const sichtbareSchichten = useMemo(() => {
    const aktiveKeys = aktiveDienstKeys(dienste);
    return schichten
      .filter(schicht => aktiveKeys.size === 0 || aktiveKeys.has(normalisiereName(schicht.JobName)))
      .flatMap(schicht => {
        const basis = {
          ...schicht,
          Tag: tagAlsString(schicht.Tag),
          UhrzeitVon: formatTime(schicht.UhrzeitVon),
          UhrzeitBis: formatTime(schicht.UhrzeitBis),
          Regelmaessig: Boolean(schicht.Regelmaessig),
          WiederholungsGruppe: schicht.WiederholungsGruppe || schicht.wiederholungsGruppe || null,
        };
        if (basis.Regelmaessig && !basis.WiederholungsGruppe) {
          return CAMP_TAGE.map(tag => ({
            ...basis,
            AnzeigeTag: tag.datum,
            AnzeigeKey: `${basis.ID}-${tag.datum}`,
          }));
        }
        return [{ ...basis, AnzeigeTag: basis.Tag, AnzeigeKey: String(basis.ID) }];
      })
      .sort(sortNachTagUndZeit);
  }, [dienste, schichten]);

  const sichtbareFahrten = useMemo(() => fahrten
    .map(fahrt => ({
      ...fahrt,
      Tag: tagAlsString(fahrt.Tag),
      Abfahrt: formatTime(fahrt.Abfahrt),
    }))
    .sort((a, b) => {
      if (a.Tag !== b.Tag) return a.Tag.localeCompare(b.Tag);
      if ((a.Reihenfolge || 0) !== (b.Reihenfolge || 0)) return (a.Reihenfolge || 0) - (b.Reihenfolge || 0);
      return String(a.Abfahrt || '').localeCompare(String(b.Abfahrt || ''));
    }), [fahrten]);

  const personen = useMemo(() => {
    const namen = new Map();
    sichtbareSchichten.forEach(schicht => {
      parsePersonen(schicht.Person).forEach(person => namen.set(normalisiereName(person), person));
    });
    sichtbareFahrten.forEach(fahrt => {
      parsePersonen(fahrt.Fahrer).forEach(person => namen.set(normalisiereName(person), person));
    });
    return Array.from(namen.values()).sort((a, b) => a.localeCompare(b, 'de'));
  }, [sichtbareFahrten, sichtbareSchichten]);

  const diensteListe = useMemo(() => Array.from(dienstInfo.values())
    .sort((a, b) => {
      if ((a.Reihenfolge || 999) !== (b.Reihenfolge || 999)) return (a.Reihenfolge || 999) - (b.Reihenfolge || 999);
      return String(a.Name || '').localeCompare(String(b.Name || ''), 'de');
    }), [dienstInfo]);

  return { schichten: sichtbareSchichten, fahrten: sichtbareFahrten, dienstInfo, diensteListe, personen, laden, fehler };
}

function gruppiereNachTag(eintraege, tagFeld = 'AnzeigeTag') {
  return eintraege.reduce((acc, eintrag) => {
    const tag = eintrag[tagFeld] || eintrag.Tag;
    if (!acc[tag]) acc[tag] = [];
    acc[tag].push(eintrag);
    return acc;
  }, {});
}

function dienstGruppen(schichten) {
  const map = new Map();
  schichten.forEach(schicht => {
    const key = `${schicht.UhrzeitVon || ''}-${schicht.UhrzeitBis || ''}-${schicht.JobName}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        job: schicht.JobName,
        von: schicht.UhrzeitVon,
        bis: schicht.UhrzeitBis,
        regelmaessig: Boolean(schicht.Regelmaessig),
        personen: [],
      });
    }
    if (schicht.Person) map.get(key).personen.push(schicht.Person);
  });
  return Array.from(map.values()).sort((a, b) => {
    if ((a.von || '') !== (b.von || '')) return String(a.von || '').localeCompare(String(b.von || ''));
    return String(a.job).localeCompare(String(b.job), 'de');
  });
}

function LoadingOrError({ laden, fehler }) {
  if (laden) return <div className="logistik-web-state">Wird geladen...</div>;
  if (fehler) return <div className="logistik-web-state error">{fehler}</div>;
  return null;
}

function ViewTabs({ active }) {
  return (
    <div className="logistik-view-tabs">
      {VIEWS.map(view => (
        <Link
          key={view.mode}
          to={view.path}
          className={`logistik-view-tab${active === view.mode ? ' active' : ''}`}
        >
          <span className="material-symbols-outlined">{view.icon}</span>
          {view.label}
        </Link>
      ))}
    </div>
  );
}

function Hub() {
  return (
    <div className="logistik-hub-grid">
      {HUB_LINKS.map(view => (
        <Link key={view.mode} to={view.path} className={`logistik-hub-card${view.secondary ? ' secondary' : ''}`}>
          <span className="material-symbols-outlined">{view.icon}</span>
          <span>{view.label}</span>
        </Link>
      ))}
    </div>
  );
}

function DienstBlock({ gruppe, dienstInfo }) {
  const info = dienstInfo.get(normalisiereName(gruppe.job));
  return (
    <div className="logistik-duty-row">
      <div className="logistik-duty-time">
        <span>{zeitraum({ UhrzeitVon: gruppe.von, UhrzeitBis: gruppe.bis }) || '-'}</span>
        {gruppe.regelmaessig && <span className="logistik-repeat" title="Wiederholung">↻</span>}
      </div>
      <div className="logistik-duty-main">
        <div className="logistik-duty-title">{gruppe.job}</div>
        {info?.Beschreibung && <div className="logistik-duty-desc">{info.Beschreibung}</div>}
        <div className="logistik-person-list">
          {gruppe.personen.length > 0 ? gruppe.personen.join(', ') : 'Noch nicht besetzt'}
        </div>
      </div>
    </div>
  );
}

function FahrtBlock({ fahrt }) {
  return (
    <div className="logistik-ride-row">
      <div className="logistik-ride-time">
        <span>{fahrt.Reihenfolge ? `Nr. ${fahrt.Reihenfolge}` : 'Fahrt'}</span>
        <small>{fahrt.Abfahrt || 'ohne Zeit'}</small>
      </div>
      <div className="logistik-ride-main">
        <div className="logistik-ride-title">
          {fahrt.Ziel || 'Fahrt'}{fahrt.Fahrzeug ? ` · ${fahrt.Fahrzeug}` : ''}
        </div>
        <div className="logistik-ride-meta">
          {fahrt.Fahrer && <span>{fahrt.Fahrer}</span>}
          {fahrt.Anschrift && <span>{fahrt.Anschrift}</span>}
        </div>
        {fahrt.Infos && <div className="logistik-ride-info">{fahrt.Infos}</div>}
      </div>
    </div>
  );
}

function DayChips({ value, onChange, allowAll = false }) {
  return (
    <div className="logistik-day-chips">
      {allowAll && (
        <button
          type="button"
          className={`logistik-day-chip${value === 'all' ? ' active' : ''}`}
          onClick={() => onChange('all')}
        >
          Alle
        </button>
      )}
      {CAMP_TAGE.map(tag => (
        <button
          key={tag.datum}
          type="button"
          className={`logistik-day-chip${value === tag.datum ? ' active' : ''}`}
          onClick={() => onChange(tag.datum)}
        >
          {tag.label}
        </button>
      ))}
    </div>
  );
}

function TagesInhalt({ tag, schichten, fahrten, dienstInfo, fahrtenKurz = false }) {
  const dienste = dienstGruppen(schichten.filter(schicht => schicht.AnzeigeTag === tag));
  const fahrtenFuerTag = fahrten.filter(fahrt => fahrt.Tag === tag);

  return (
    <div className="logistik-day-content">
      {dienste.length === 0 ? (
        <div className="logistik-empty">Keine Dienste eingetragen.</div>
      ) : (
        <div className="logistik-list">
          {dienste.map(gruppe => <DienstBlock key={gruppe.key} gruppe={gruppe} dienstInfo={dienstInfo} />)}
        </div>
      )}
      {fahrtenFuerTag.length > 0 && (
        <div className="logistik-subsection">
          <h3>Fahrten</h3>
          <div className="logistik-list">
            {fahrtenFuerTag.slice(0, fahrtenKurz ? 3 : fahrtenFuerTag.length).map(fahrt => (
              <FahrtBlock key={fahrt.ID} fahrt={fahrt} />
            ))}
          </div>
          {fahrtenKurz && fahrtenFuerTag.length > 3 && (
            <Link className="logistik-inline-link" to="/dienstplaene/logistik/fahrten">
              Alle Fahrten anzeigen
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function PersonSucheView({ daten }) {
  const [suche, setSuche] = useState('');
  const query = suche.trim();
  const namensTreffer = daten.personen.filter(person => normalisiereName(person).includes(normalisiereName(query)));
  const schichten = query.length >= 2
    ? daten.schichten.filter(schicht => personPasst(schicht.Person, query))
    : [];
  const fahrten = query.length >= 2
    ? daten.fahrten.filter(fahrt => personPasst(fahrt.Fahrer, query))
    : [];
  const schichtenNachTag = gruppiereNachTag(schichten);
  const fahrtenNachTag = gruppiereNachTag(fahrten, 'Tag');
  const tage = CAMP_TAGE
    .map(tag => tag.datum)
    .filter(tag => schichtenNachTag[tag]?.length || fahrtenNachTag[tag]?.length);

  return (
    <>
      <div className="logistik-search-panel">
        <label htmlFor="person-suche">Name</label>
        <input
          id="person-suche"
          type="text"
          className="logistik-search-input"
          value={suche}
          onChange={event => setSuche(event.target.value)}
          placeholder="Name eingeben"
          autoComplete="off"
        />
      </div>
      {query.length < 2 && daten.personen.length > 0 && (
        <div className="logistik-person-chips">
          {daten.personen.map(person => (
            <button key={person} type="button" className="logistik-person-chip" onClick={() => setSuche(person)}>
              {person}
            </button>
          ))}
        </div>
      )}
      {query.length >= 2 && namensTreffer.length > 0 && (
        <div className="logistik-person-chips compact">
          {namensTreffer.slice(0, 8).map(person => (
            <button key={person} type="button" className="logistik-person-chip" onClick={() => setSuche(person)}>
              {person}
            </button>
          ))}
        </div>
      )}
      {query.length >= 2 && tage.length === 0 && (
        <div className="logistik-empty">Keine Dienste oder Fahrten gefunden.</div>
      )}
      {tage.map(tag => (
        <section key={tag} className="logistik-result-section">
          <h2>{tagInfo(tag).lang}</h2>
          {schichtenNachTag[tag]?.length > 0 && (
            <div className="logistik-list">
              {dienstGruppen(schichtenNachTag[tag]).map(gruppe => (
                <DienstBlock key={gruppe.key} gruppe={gruppe} dienstInfo={daten.dienstInfo} />
              ))}
            </div>
          )}
          {fahrtenNachTag[tag]?.length > 0 && (
            <div className="logistik-list">
              {fahrtenNachTag[tag].map(fahrt => <FahrtBlock key={fahrt.ID} fahrt={fahrt} />)}
            </div>
          )}
        </section>
      ))}
    </>
  );
}

function TagSucheView({ daten }) {
  const [selectedTag, setSelectedTag] = useState(heuteOderErsterTag());

  return (
    <>
      <DayChips value={selectedTag} onChange={setSelectedTag} />
      <section className="logistik-result-section">
        <h2>{tagInfo(selectedTag).lang}</h2>
        <TagesInhalt
          tag={selectedTag}
          schichten={daten.schichten}
          fahrten={daten.fahrten}
          dienstInfo={daten.dienstInfo}
          fahrtenKurz
        />
      </section>
    </>
  );
}

function DienstSucheView({ daten }) {
  const [suche, setSuche] = useState('');
  const query = normalisiereName(suche);
  const dienstTreffer = daten.diensteListe.filter(dienst => normalisiereName(dienst.Name).includes(query));
  const aktiveDienste = query ? dienstTreffer : daten.diensteListe;
  const selectedDienst = query && dienstTreffer.length === 1 ? dienstTreffer[0] : null;
  const schichten = query
    ? daten.schichten.filter(schicht => normalisiereName(schicht.JobName).includes(query))
    : [];
  const schichtenNachTag = gruppiereNachTag(schichten);
  const tage = CAMP_TAGE
    .map(tag => tag.datum)
    .filter(tag => schichtenNachTag[tag]?.length);

  return (
    <>
      <div className="logistik-search-panel">
        <label htmlFor="dienst-suche">Dienst</label>
        <input
          id="dienst-suche"
          type="text"
          className="logistik-search-input"
          value={suche}
          onChange={event => setSuche(event.target.value)}
          placeholder="Dienstname eingeben"
          autoComplete="off"
        />
      </div>
      <div className="logistik-person-chips compact">
        {aktiveDienste.slice(0, query ? 10 : daten.diensteListe.length).map(dienst => (
          <button key={`${dienst.ScopeKey || 'ALL'}-${dienst.Name}`} type="button" className="logistik-person-chip" onClick={() => setSuche(dienst.Name)}>
            {dienst.Name}
          </button>
        ))}
      </div>
      {selectedDienst?.Beschreibung && (
        <div className="logistik-service-note">
          <span>{selectedDienst.Soll || 'Dienst'}</span>
          <p>{selectedDienst.Beschreibung}</p>
        </div>
      )}
      {query && tage.length === 0 && (
        <div className="logistik-empty">Keine Einträge für diesen Dienst gefunden.</div>
      )}
      {tage.map(tag => (
        <section key={tag} className="logistik-result-section">
          <h2>{tagInfo(tag).lang}</h2>
          <div className="logistik-list">
            {dienstGruppen(schichtenNachTag[tag]).map(gruppe => (
              <DienstBlock key={gruppe.key} gruppe={gruppe} dienstInfo={daten.dienstInfo} />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function GesamtuebersichtView({ daten }) {
  const startTag = heuteOderErsterTag();
  return (
    <div className="logistik-overview-list">
      {CAMP_TAGE.map(tag => {
        const hatDienste = daten.schichten.some(schicht => schicht.AnzeigeTag === tag.datum);
        const hatFahrten = daten.fahrten.some(fahrt => fahrt.Tag === tag.datum);
        return (
          <details key={tag.datum} className="logistik-day-details" open={tag.datum === startTag || hatDienste}>
            <summary>
              <span>{tag.lang}</span>
              <small>{hatDienste || hatFahrten ? 'Einträge vorhanden' : 'leer'}</small>
            </summary>
            <TagesInhalt
              tag={tag.datum}
              schichten={daten.schichten}
              fahrten={daten.fahrten}
              dienstInfo={daten.dienstInfo}
              fahrtenKurz
            />
          </details>
        );
      })}
      <details className="logistik-matrix-access">
        <summary>Weitere Ansicht</summary>
        <Link to="/dienstplaene/logistik/matrix">Matrixansicht öffnen</Link>
      </details>
    </div>
  );
}

function MatrixView({ daten }) {
  const [selectedTag, setSelectedTag] = useState(heuteOderErsterTag());
  const schichten = daten.schichten.filter(schicht => schicht.AnzeigeTag === selectedTag);
  const zeiten = Array.from(new Set(schichten.map(schicht => schicht.UhrzeitVon).filter(Boolean))).sort();
  const diensteMitEintraegen = new Set(schichten.map(schicht => normalisiereName(schicht.JobName)));
  const dienste = daten.diensteListe.filter(dienst => diensteMitEintraegen.has(normalisiereName(dienst.Name)));

  return (
    <>
      <Link className="logistik-inline-link" to="/dienstplaene/logistik/gesamt">
        Zur Gesamtübersicht
      </Link>
      <DayChips value={selectedTag} onChange={setSelectedTag} />
      {zeiten.length === 0 || dienste.length === 0 ? (
        <div className="logistik-empty">Keine Matrix-Einträge für diesen Tag.</div>
      ) : (
        <div className="logistik-matrix-wrapper">
          <table className="logistik-matrix-table">
            <thead>
              <tr>
                <th>Zeit</th>
                {dienste.map(dienst => (
                  <th key={dienst.Name}>{dienst.Name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zeiten.map(zeit => (
                <tr key={zeit}>
                  <th>{zeit}</th>
                  {dienste.map(dienst => {
                    const eintraege = schichten.filter(schicht => schicht.UhrzeitVon === zeit && normalisiereName(schicht.JobName) === normalisiereName(dienst.Name));
                    return (
                      <td key={`${zeit}-${dienst.Name}`} className={eintraege.length > 0 ? 'filled' : ''}>
                        {eintraege.map(eintrag => (
                          <div key={eintrag.AnzeigeKey} className="logistik-matrix-entry">
                            <strong>{parsePersonen(eintrag.Person).join(', ') || '-'}</strong>
                            <span>{zeitraum(eintrag)}</span>
                          </div>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function FahrtenView({ daten }) {
  const [selectedTag, setSelectedTag] = useState(heuteOderErsterTag());
  const [suche, setSuche] = useState('');
  const query = normalisiereName(suche);
  const fahrten = daten.fahrten.filter(fahrt => {
    const tagPasst = selectedTag === 'all' || fahrt.Tag === selectedTag;
    const text = normalisiereName(`${fahrt.Fahrer || ''} ${fahrt.Fahrzeug || ''} ${fahrt.Ziel || ''} ${fahrt.Anschrift || ''} ${fahrt.Infos || ''}`);
    return tagPasst && (!query || text.includes(query));
  });

  return (
    <>
      <DayChips value={selectedTag} onChange={setSelectedTag} allowAll />
      <div className="logistik-search-panel">
        <label htmlFor="fahrten-suche">Suche</label>
        <input
          id="fahrten-suche"
          type="text"
          className="logistik-search-input"
          value={suche}
          onChange={event => setSuche(event.target.value)}
          placeholder="Fahrer, Ziel, Fahrzeug"
        />
      </div>
      {fahrten.length === 0 ? (
        <div className="logistik-empty">Keine Fahrten gefunden.</div>
      ) : (
        <div className="logistik-list">
          {fahrten.map(fahrt => (
            <div key={fahrt.ID} className="logistik-ride-card">
              <div className="logistik-ride-date">{tagInfo(fahrt.Tag).label}</div>
              <FahrtBlock fahrt={fahrt} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function LogistikDienstplaene({ mode = 'hub' }) {
  const daten = useLogistikDaten();
  const title = mode === 'hub'
    ? 'Logistik-Dienstpläne'
    : mode === 'matrix'
      ? 'Matrixansicht'
      : VIEWS.find(view => view.mode === mode)?.label || 'Logistik-Dienstpläne';
  const status = <LoadingOrError laden={daten.laden} fehler={daten.fehler} />;
  const showContent = !daten.laden && !daten.fehler;

  return (
    <main className="logistik-dienstplaene-page">
      <div className="page-header">
        <BackArrow to={mode === 'hub' ? '/' : mode === 'matrix' ? '/dienstplaene/logistik/gesamt' : '/dienstplaene'} />
        <h1>{title}</h1>
      </div>

      {mode !== 'hub' && mode !== 'matrix' && <ViewTabs active={mode} />}
      {status}

      {mode === 'hub' && <Hub />}
      {showContent && mode === 'person' && <PersonSucheView daten={daten} />}
      {showContent && mode === 'tag' && <TagSucheView daten={daten} />}
      {showContent && mode === 'dienst' && <DienstSucheView daten={daten} />}
      {showContent && mode === 'gesamt' && <GesamtuebersichtView daten={daten} />}
      {showContent && mode === 'matrix' && <MatrixView daten={daten} />}
      {showContent && mode === 'fahrten' && <FahrtenView daten={daten} />}
    </main>
  );
}

export default LogistikDienstplaene;
